import {Component, effect, inject, OnInit, signal, output} from '@angular/core';
import {TranslocoDirective, TranslocoPipe} from '@jsverse/transloco';
import {BadgeListComponent} from '../../../../../../../shared/components/list/badge-list/badge-list.component';
import {BadgeListItem} from '../../../../../../../shared/components/list/badge-list/badge-list.types';
import {ProjectTimeFrame} from './project-time-frame.type';
import {ProjectTimeFrameFormComponent} from './project-time-frame-form/project-time-frame-form.component';
import {ProjectTimeFrameService} from './step-1-2-project-time-frame.service';
import {ProjectTimeFrameResponse} from '@frontend/shared-api';
import {Step12ProjectCharterService} from '../step-1-2-project-charter.service';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';
import {ProjectBaseStepComponent} from '../../../../project-base-step/project-base-step.component';
import {Button} from 'primeng/button';
import {ConfirmStepLeaveDirective} from '../../../../../../../shared/project-step-save-button/confirm-step-leave.directive';
import {Dialog} from 'primeng/dialog';
import {FormsModule} from '@angular/forms';
import {DatePicker} from 'primeng/datepicker';
import {CardTitleComponent} from '../../../../../../../shared/components/header/card-title/card-title.component';
import {LoadingComponent} from '../../../../../../../shared/components/loading/loading.component';

@Component({
  selector: 'app-step-1-2-project-charter-time-frame',
  imports: [
    TranslocoDirective,
    TranslocoPipe,
    BadgeListComponent,
    ProjectTimeFrameFormComponent,
    Button,
    ConfirmStepLeaveDirective,
    Dialog,
    FormsModule,
    DatePicker,
    CardTitleComponent,
    LoadingComponent,
  ],
  templateUrl: './step-1-2-project-charter-time-frame.component.html',
})
export class Step12ProjectCharterTimeFrameComponent
  extends ProjectBaseStepComponent
  implements OnInit
{
  protected timeFrameService = inject(ProjectTimeFrameService);
  protected charterService = inject(Step12ProjectCharterService);
  readonly badgeListItems = signal<BadgeListItem[]>([]);

  readonly statusChange = output<{
    totalSections: number;
    completedSections: number;
  }>();

  readonly showSuggestDialog = signal(false);
  readonly projectStartDate = signal<Date>(new Date());
  readonly suggestedDate = signal<Date | null>(null);

  constructor() {
    super();
    effect(() => {
      this.createBadgeList(this.timeFrameService.mergedTimeFrames());
    });
    effect(() => {
      this.projectStartDate.set(this.timeFrameService.loadProjectStartDate() || new Date());
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this.timeFrameService.loadTimeFrames();
  }

  private createBadgeList(timeFrameData: ProjectTimeFrame[]): void {
    const items: BadgeListItem[] = [];

    const sortedTimeFrameData = [...timeFrameData].sort((a, b) => {
      const aNum = a.tfNumber ?? Number.MAX_SAFE_INTEGER;
      const bNum = b.tfNumber ?? Number.MAX_SAFE_INTEGER;
      return aNum - bNum;
    });
    sortedTimeFrameData.forEach((timeFrame: ProjectTimeFrame, index: number) => {
      const identifier = 'TF' + timeFrame.tfNumber;
      const precedingTimeFrame = index > 0 ? sortedTimeFrameData[index - 1] : undefined;
      items.push({
        key: String(timeFrame.tfNumber),
        identifier: identifier,
        originalItem: timeFrame,
        translations: timeFrame.translations,
        metadata: {precedingTimeFrame},
        collapsed: !(timeFrame.suggested && timeFrame.mandatory),
      });
    });

    this.badgeListItems.set(items);
  }

  updateBadgeListWithSavedTimeFrame(response: ProjectTimeFrameResponse): void {
    const items = this.badgeListItems();
    const idx = items.findIndex((i) => i.key === String(response.tfNumber));
    if (idx < 0) return;

    const updatedItems: BadgeListItem[] = [...items];
    const originalItem = items[idx].originalItem;

    updatedItems[idx] = {
      ...items[idx],
      originalItem: {
        ...originalItem,
        suggested: false,
        ...response,
      },
    };

    this.badgeListItems.set(updatedItems);
  }

  async onSaveTimeFrame($event: ProjectTimeFrameResponse) {
    this.charterService.loadCharter();
    this.updateBadgeListWithSavedTimeFrame($event);
    this.emitStatusChange();
    await this.stepState.loadStepStatuses(this.projectId);
  }

  protected emitStatusChange(): void {
    const totalSections = this.timeFrameService.getNumTotalSections();
    const completedSections = this.timeFrameService.getNumCompletedSections();
    const tabStatus: TabStatus = {totalSections, completedSections};
    this.statusChange.emit(tabStatus);
  }

  onSuggestTimeFrame() {
    this.showSuggestDialog.set(true);
    this.suggestedDate.set(this.projectStartDate());
  }

  onCancelSuggest() {
    this.showSuggestDialog.set(false);
    this.suggestedDate.set(null);
  }

  onConfirmSuggest() {
    const date = this.suggestedDate();
    if (date) {
      this.timeFrameService.suggestTimeFrames(date);
      this.showSuggestDialog.set(false);
    }
  }
}
